import { BoundSelectField, BoundSelectFieldProps, HasIdAndName, Optional, useComputed, Value } from "@homebound/beam";
import { ObjectState } from "src/utils/formState";
import { AiFieldSources, AiProposalValue, pendingSources } from "./AiProposalValue";
import { AiFieldProposalFormValue } from "./utils/aiFieldProposalForm";
import { AiProposalParser, parseAiString, pendingProposedValue, resolveAiProposalStatus } from "./utils/bindAiProposal";

/** Like Beam's `{ id, name }` overload of `BoundSelectField`, so callers can skip `getOptionValue`/`getOptionLabel`. */
export type BoundAiSelectFieldProps<O extends HasIdAndName<V>, V extends Value> = Optional<
  // Derive proposedValue from aiProposal so callers cannot supply a conflicting value.
  Omit<BoundSelectFieldProps<O, V>, "proposedValue">,
  "getOptionLabel" | "getOptionValue"
> & {
  aiProposal?: ObjectState<AiFieldProposalFormValue>;
  /**
   * Coerce GraphQL's string `fieldValue` into the select's value type.
   * Defaults to the raw string (enum codes). Pass a lookup when the proposal is a name and the field is an id.
   */
  parser?: AiProposalParser<V>;
};

export function BoundAiSelectField<O extends HasIdAndName<V>, V extends Value>(props: BoundAiSelectFieldProps<O, V>) {
  const { field, aiProposal, helperText, onSelect, parser = parseAiString as AiProposalParser<V>, ...others } = props;
  // Track proposal status and fieldValue so the pending proposal stays in sync.
  const proposedValue = useComputed(() => pendingProposedValue(aiProposal, parser), [aiProposal, parser]);
  const { options: source, getOptionValue, getOptionLabel, unsetLabel } = others;
  const originalValue = useComputed(() => {
    const current = Array.isArray(source) ? undefined : source.current;
    const options = Array.isArray(source)
      ? source
      : [...(source.options ?? []), ...(Array.isArray(current) ? current : current ? [current] : [])];
    const option = options.find((o) => (getOptionValue ? getOptionValue(o) : o.id) === field.originalValue);
    return option ? (getOptionLabel?.(option) ?? option.name) : unsetLabel;
  }, [field, source, getOptionValue, getOptionLabel, unsetLabel]);
  const sources = useComputed(() => pendingSources(aiProposal), [aiProposal]);
  return (
    <AiProposalValue aiProposal={aiProposal} originalValue={originalValue}>
      <BoundSelectField
        helperText={sources ? <AiFieldSources {...sources} /> : helperText}
        field={field}
        proposedValue={proposedValue}
        onSelect={(value, opt) => {
          field.set(value);
          resolveAiProposalStatus(aiProposal, parser, value);
          onSelect?.(value, opt);
        }}
        {...others}
      />
    </AiProposalValue>
  );
}
